/**
 * A textarea with an @mention typeahead.
 *
 * ## What the user sees vs. what gets stored
 *
 * The user types and reads plain text: `@Ann Lee please clear customs`. The stored format
 * carries the user id (`@[Ann Lee](user:abc-123)`) because the id — not the name — is the
 * authority for the access grant. Those must not be the same string: an editor that shows
 * `@[Acme Manager](user:1770226980829-96zpfu623)` mid-sentence is unusable and leaks an
 * internal identifier into the UI.
 *
 * So this component is controlled on DISPLAY text, and tracks the mentioned ids alongside it
 * (`mentionedUsers`). The caller converts to marker text with `toMarkerText()` at submit.
 *
 * That split fails safe: edit or delete a mention's name and it simply stops being a mention
 * — the id is dropped and nobody gains access. A grant can never outlive the name that
 * justified it.
 *
 * A contenteditable rich editor was deliberately avoided: it maintains its own DOM model of
 * the mentions, and any drift between that model and the text is drift between what the user
 * sees and who actually got access.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Textarea,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@lognai/synapse-design-system-v2';
import { sanitizeMentionLabel, type MentionedUser } from '@lognai/plugin-sdk';
import { useAssignableUsers } from '../../hooks/use-task-hooks';
import type { AssignableUser } from '../../types/tasks';

export interface MentionTextareaProps {
  /** DISPLAY text — what the user reads. Never contains markers or ids. */
  value: string;
  onChange: (displayText: string) => void;
  /** The ids behind the @names currently in `value`. Pass to `toMarkerText` on submit. */
  mentionedUsers: MentionedUser[];
  onMentionedUsersChange: (mentionedUsers: MentionedUser[]) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}

/** The `@word` the caret currently sits in, if any. */
interface ActiveMentionQuery {
  searchQuery: string;
  /** Index of the '@' in the display text. */
  triggerIndex: number;
}

const findActiveMentionQuery = (
  displayText: string,
  caretIndex: number
): ActiveMentionQuery | null => {
  const textBeforeCaret = displayText.slice(0, caretIndex);

  // The '@' must start a word. The query may contain spaces (names do), but stopping at two
  // words keeps the popup from staying open across a whole sentence.
  const match = /(^|\s)@([\p{L}\p{N}._-]*(?: [\p{L}\p{N}._-]*)?)$/u.exec(textBeforeCaret);
  if (!match) return null;

  return {
    searchQuery: match[2],
    triggerIndex: caretIndex - match[2].length - 1,
  };
};

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  mentionedUsers,
  onMentionedUsersChange,
  placeholder,
  rows = 3,
  disabled,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [activeMentionQuery, setActiveMentionQuery] = useState<ActiveMentionQuery | null>(null);

  const { data: assignableUsers } = useAssignableUsers(activeMentionQuery?.searchQuery);

  const suggestions = useMemo(
    () => (activeMentionQuery ? (assignableUsers ?? []).slice(0, 8) : []),
    [activeMentionQuery, assignableUsers]
  );

  /** Drop ids whose `@Name` no longer appears — the mention was edited or deleted away. */
  const pruneStaleMentions = useCallback(
    (displayText: string, currentMentions: MentionedUser[]): MentionedUser[] =>
      currentMentions.filter((mentionedUser) =>
        displayText.includes(`@${mentionedUser.label}`)
      ),
    []
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const nextDisplayText = event.target.value;

      onChange(nextDisplayText);

      const survivingMentions = pruneStaleMentions(nextDisplayText, mentionedUsers);
      if (survivingMentions.length !== mentionedUsers.length) {
        onMentionedUsersChange(survivingMentions);
      }

      setActiveMentionQuery(
        findActiveMentionQuery(
          nextDisplayText,
          event.target.selectionStart ?? nextDisplayText.length
        )
      );
    },
    [onChange, mentionedUsers, onMentionedUsersChange, pruneStaleMentions]
  );

  const insertMention = useCallback(
    (user: AssignableUser) => {
      if (!activeMentionQuery) return;

      const { triggerIndex, searchQuery } = activeMentionQuery;
      // Sanitized here too: the label is matched back out of the text at submit, and a name
      // containing the marker delimiters would make that ambiguous.
      const label = sanitizeMentionLabel(user.fullName ?? user.email);

      const nextDisplayText =
        value.slice(0, triggerIndex) +
        `@${label} ` +
        value.slice(triggerIndex + 1 + searchQuery.length);

      onChange(nextDisplayText);
      onMentionedUsersChange([
        ...mentionedUsers.filter((mentioned) => mentioned.userId !== user.id),
        { userId: user.id, label },
      ]);
      setActiveMentionQuery(null);

      // Keep the caret after the inserted name rather than letting it jump to the end.
      const caretAfterMention = triggerIndex + label.length + 2;
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
        textareaRef.current?.setSelectionRange(caretAfterMention, caretAfterMention);
      });
    },
    [activeMentionQuery, value, onChange, mentionedUsers, onMentionedUsersChange]
  );

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onBlur={() => {
          // Delay so a click on a suggestion still lands.
          setTimeout(() => setActiveMentionQuery(null), 150);
        }}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
      />

      {activeMentionQuery && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-72 rounded-md border border-border bg-popover">
          <Command>
            <CommandList>
              <CommandEmpty>No matching people.</CommandEmpty>
              <CommandGroup heading="Mention someone">
                {suggestions.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={`${user.fullName ?? ''} ${user.email}`}
                    onSelect={() => insertMention(user)}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">{user.fullName ?? user.email}</span>
                      <span className="text-xs text-muted-foreground">{user.email}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
};
